'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Sparkles, Play, Video, Image as ImageIcon, FileIcon } from 'lucide-react'
import { getAsset } from '@/lib/api-v3'
import { VIDEO_BLANK_POSTER } from '@/lib/utils'
import { getTokens } from '@/lib/auth'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface AIVideoMetadata {
  additionalContentId: number
  start: number
  end: number
  nextStartTime: number
  isAi: boolean
  index: number
  totalIndexes: number
  localPath: string
  overwrittenStart?: number | null
  overwrittenEnd?: number | null
}

export interface SubtitleWord {
  word: string
  startTime: number
  endTime: number
}

export interface StepAttachmentItem {
  id: number
  type: 'image' | 'video' | 'audio'
  url: string
  alt: string
  duration?: string
  aiMetadata?: AIVideoMetadata
  subtitlesId?: number | null
}

// ── Utils ──────────────────────────────────────────────────────────────────────

const SUBTITLE_SILENCE_GAP = 2.0
const SUBTITLE_WORDS_PER_LINE = 7
const SUBTITLE_MAX_VISIBLE = SUBTITLE_WORDS_PER_LINE * 2

/**
 * YouTube-логика окна субтитров: берём последние два ряда произнесённых слов.
 * Алгоритм идентичен странице процедуры.
 */
export function getVisibleSubtitleWords(words: SubtitleWord[], t: number): SubtitleWord[] {
  let end = words.length - 1
  while (end >= 0 && words[end].startTime > t) end--
  if (end < 0) return []
  const spokenWords = words.slice(0, end + 1)
  const last = spokenWords[spokenWords.length - 1]
  if (t - last.endTime > SUBTITLE_SILENCE_GAP) return []
  let start = spokenWords.length - 1
  while (start > 0) {
    const gap = spokenWords[start].startTime - spokenWords[start - 1].endTime
    if (gap >= SUBTITLE_SILENCE_GAP) break
    start--
  }
  const phrase = spokenWords.slice(start)
  const lines: SubtitleWord[][] = []
  for (let i = 0; i < phrase.length; i += SUBTITLE_WORDS_PER_LINE) {
    lines.push(phrase.slice(i, i + SUBTITLE_WORDS_PER_LINE))
  }
  return lines.slice(-(SUBTITLE_MAX_VISIBLE / SUBTITLE_WORDS_PER_LINE)).flat()
}

export function decodeAIMetadata(metadata: unknown): AIVideoMetadata | null {
  if (!metadata) return null
  try {
    if (typeof metadata === 'object' && metadata !== null && (metadata as AIVideoMetadata).isAi) {
      return metadata as AIVideoMetadata
    }
    if (typeof metadata === 'string') {
      const parsed = JSON.parse(atob(metadata))
      if (parsed.isAi) return parsed as AIVideoMetadata
    }
    return null
  } catch {
    return null
  }
}

/**
 * Отрезок файла видео, который показывает вложение (секунды): окно AI-шага или обрезка
 * обычного видео; null — показывается весь файл.
 */
export function videoAttachmentWindow(metadata: unknown): { start: number; end: number } | null {
  const ai = decodeAIMetadata(metadata)
  if (ai) {
    return {
      start: ai.overwrittenStart ?? Math.max(0, ai.start - 0.5),
      end: ai.overwrittenEnd ?? (ai.nextStartTime > 0 ? ai.nextStartTime - 0.5 : ai.end),
    }
  }
  const trim = decodeRegularVideoTrim(metadata)
  return trim ? { start: trim.overwrittenStart, end: trim.overwrittenEnd } : null
}

export function decodeRegularVideoTrim(
  metadata: unknown
): { overwrittenStart: number; overwrittenEnd: number } | null {
  if (!metadata) return null
  try {
    const parsed =
      typeof metadata === 'string' ? JSON.parse(atob(metadata)) : (metadata as any)
    if (parsed?.isRegularVideoTrim) {
      return { overwrittenStart: parsed.overwrittenStart, overwrittenEnd: parsed.overwrittenEnd }
    }
    return null
  } catch {
    return null
  }
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export function getVideoDurationSec(videoUrl: string): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      const d = video.duration
      resolve(isNaN(d) ? 0 : d)
    }
    video.onerror = () => resolve(0)
    video.src = videoUrl
  })
}

// ── useLazyVideoSrc ────────────────────────────────────────────────────────────
// Откладывает загрузку видео до появления контейнера в viewport (или 100px до него).
// Срабатывает ровно один раз на монтирование — сразу отписывается от observer.
// При анмаунте (смена чата / страницы) observer автоматически дисконнектится,
// поэтому повторного срабатывания при возврате не будет — только при следующем монте.
function useLazyVideoSrc(
  containerRef: React.RefObject<HTMLElement | null>,
  url: string,
  alt: string,
): { lazySrc: string | null; onVideoLoaded: () => void } {
  const [lazySrc, setLazySrc] = useState<string | null>(null)
  const triggeredRef = useRef(false)

  useEffect(() => {
    if (triggeredRef.current) return
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !triggeredRef.current) {
          triggeredRef.current = true
          console.log(
            `%c[Copilot] 🎬 Вложение "${alt}" — появилось в кадре, начинаем загрузку`,
            'color:#3b82f6;font-weight:bold',
          )
          setLazySrc(url)
          observer.disconnect()
        }
      },
      { rootMargin: '100px', threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
    // containerRef стабилен — намеренно не включаем в deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, alt])

  const onVideoLoaded = useCallback(() => {
    console.log(
      `%c[Copilot] ✅ Загрузка завершена: "${alt}"`,
      'color:#22c55e;font-weight:bold',
    )
  }, [alt])

  return { lazySrc, onVideoLoaded }
}

// ── AIVideoPreview ─────────────────────────────────────────────────────────────
// Превью-кадр AI-видео — перематывает к effectiveStart при загрузке метаданных.

export function AIVideoPreview({
  src,
  aiMetadata,
  className,
  onLoaded,
}: {
  src: string
  aiMetadata: AIVideoMetadata
  className?: string
  onLoaded?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const effectiveStart = aiMetadata.overwrittenStart ?? aiMetadata.start
    const updateTime = () => {
      if (video.readyState < 1) return
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked)
        video.removeAttribute('poster')
      }
      video.addEventListener('seeked', onSeeked)
      video.currentTime = effectiveStart
    }
    if (video.readyState >= 1) {
      updateTime()
    } else {
      video.addEventListener('loadedmetadata', updateTime)
      return () => video.removeEventListener('loadedmetadata', updateTime)
    }
  }, [aiMetadata.start, aiMetadata.overwrittenStart, src])

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={videoRef}
      src={src}
      className={className}
      preload="metadata"
      playsInline
      poster={VIDEO_BLANK_POSTER}
      style={{ pointerEvents: 'none' }}
      onLoadedMetadata={onLoaded}
    />
  )
}

// ── AIVideoPlayer ──────────────────────────────────────────────────────────────
// Единый десктопный плеер (AI-обрезка, субтитры, скорость) — см. desktop-video-player.tsx.

export { DesktopVideoPlayer, DesktopVideoPlayer as AIVideoPlayer } from './desktop-video-player'

// ── LazyVideoThumb ─────────────────────────────────────────────────────────────
// Внутренний компонент: отображает видео-превью только после попадания в viewport.
// До этого момента рендерит иконку-плейсхолдер — браузер не тратит трафик на видео.
// Использует useLazyVideoSrc: один IntersectionObserver на монтирование, без повторов.
function LazyVideoThumb({
  attachment,
  index,
  onOpen,
}: {
  attachment: StepAttachmentItem
  index: number
  onOpen: (index: number) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { lazySrc, onVideoLoaded } = useLazyVideoSrc(
    containerRef,
    attachment.url,
    attachment.alt,
  )

  return (
    <div
      ref={containerRef}
      onClick={() => attachment.url && onOpen(index)}
      className="relative rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
      style={{ height: '135px', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px' }}
    >
      {/* Спиннер всегда под видеотегом — z-0, видео z-[1] */}
      <div className="absolute inset-0 z-0 flex items-center justify-center">
        <div className="w-7 h-7 rounded-full border-4 border-muted-foreground/20 border-t-muted-foreground animate-spin" />
      </div>

      {!lazySrc ? null : attachment.aiMetadata?.isAi ? (
        <div className="relative z-[1] h-full">
          <AIVideoPreview
            src={lazySrc}
            aiMetadata={attachment.aiMetadata}
            className="h-full w-auto rounded-lg"
            onLoaded={onVideoLoaded}
          />
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={lazySrc}
          className="relative z-[1] h-full w-auto rounded-lg"
          preload="metadata"
          playsInline
          poster={VIDEO_BLANK_POSTER}
          style={{ pointerEvents: 'none' }}
          onLoadedMetadata={(e) => {
            onVideoLoaded()
            const v = e.currentTarget
            const onSeeked = () => {
              v.removeEventListener('seeked', onSeeked)
              v.removeAttribute('poster')
            }
            v.addEventListener('seeked', onSeeked)
            try { v.currentTime = 0.001 } catch { /* ignore */ }
          }}
        />
      )}

      {attachment.duration && (
        <div
          className="absolute bottom-2 right-2 z-[2] bg-black/75 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1"
          style={{ transform: 'translateZ(0)' }}
        >
          <Play className="h-3 w-3" />
          <span>{attachment.duration}</span>
        </div>
      )}
    </div>
  )
}

// ── ScenarioAttachmentThumb ────────────────────────────────────────────────────
// Кликабельный превью-тайл вложения (135px), идентичный режиму просмотра страницы процедуры.
// Для видео использует LazyVideoThumb — загрузка только при попадании в viewport.

export const ScenarioAttachmentThumb = React.memo(function ScenarioAttachmentThumb({
  attachment,
  index,
  onOpen,
}: {
  attachment: StepAttachmentItem
  index: number
  onOpen: (index: number) => void
}) {
  return (
    <div className="relative isolate">
      {!attachment.url ? (
        <div
          className="relative rounded-lg overflow-hidden bg-muted"
          style={{ height: '135px', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px' }}
        >
          <div className="w-[135px] h-full flex flex-col items-center justify-center text-muted-foreground shrink-0">
            {attachment.type === 'image' ? (
              <ImageIcon className="h-8 w-8" />
            ) : attachment.type === 'video' ? (
              <Video className="h-8 w-8" />
            ) : (
              <FileIcon className="h-8 w-8" />
            )}
          </div>
        </div>
      ) : attachment.type === 'image' ? (
        <div
          onClick={() => onOpen(index)}
          className="relative rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
          style={{ height: '135px', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px' }}
        >
          <img
            src={attachment.url}
            alt={attachment.alt}
            className="h-full w-auto rounded-lg"
          />
        </div>
      ) : attachment.type === 'video' ? (
        <LazyVideoThumb attachment={attachment} index={index} onOpen={onOpen} />
      ) : (
        <div
          className="relative rounded-lg overflow-hidden bg-muted"
          style={{ height: '135px', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px' }}
        >
          <div className="w-[135px] h-full flex flex-col items-center justify-center text-muted-foreground shrink-0">
            <FileIcon className="h-8 w-8" />
          </div>
        </div>
      )}
      {attachment.aiMetadata?.isAi && (
        <div
          className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center z-10"
          style={{ boxShadow: 'rgba(0, 0, 0, 0.15) 0px 0px 7px 1px' }}
        >
          <Sparkles className="h-3 w-3 text-white" />
        </div>
      )}
    </div>
  )
})

// ── ScenarioAttachmentLightbox ─────────────────────────────────────────────────
// Re-exported from the unified MediaLightbox. Converts StepAttachmentItem → MediaLightboxItem.

import { MediaLightbox, MobileMediaLightbox, type MediaLightboxItem } from './media-lightbox'

export const ScenarioAttachmentLightbox = React.memo(function ScenarioAttachmentLightbox({
  items,
  initialIndex,
  onClose,
}: {
  items: StepAttachmentItem[]
  initialIndex: number
  onClose: () => void
}) {
  const lightboxItems: MediaLightboxItem[] = items
    .filter(it => it.type === 'image' || it.type === 'video')
    .map(it => ({
      id: it.id,
      src: it.url,
      type: it.type as 'image' | 'video',
      alt: it.alt,
      subtitlesId: it.subtitlesId,
      aiMetadata: it.aiMetadata,
    }))

  // На мобильных (в т.ч. в мобильном копилоте) — единый мобильный лайтбокс с полноэкранным
  // видеоплеером (перемотка, чёрный фон), а не десктопный с обычным <video controls>/AIVideoPlayer.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  const Lightbox = isMobile ? MobileMediaLightbox : MediaLightbox

  return (
    <Lightbox
      items={lightboxItems}
      initialIndex={initialIndex}
      onClose={onClose}
    />
  )
})
